import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Alert, Spinner, Badge, InputGroup
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { API_BASE_URL } from "../constants/api";

export default function EditProfile() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    
    const [user, setUser] = useState({
        nom_utilisateur: '',
        prenom_utilisateur: '',
        email: '',
        telephone: '',
        adresse: '',
        role: ''
    });

    useEffect(() => {
        fetchProfile();
    }, []);

    // Récupérer l'utilisateur depuis localStorage
    const getCurrentUser = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (userStr && userStr !== "undefined" && userStr !== "null") {
                return JSON.parse(userStr);
            }
        } catch (error) {
            console.error("Erreur parsing utilisateur:", error);
            localStorage.removeItem("user");
        }
        return null;
    };

    const fetchProfile = async () => {
        try {
            setLoading(true);
            
            const currentUser = getCurrentUser();
            
            if (!currentUser) {
                navigate('/pages/signin2');
                return;
            }

            const userId = currentUser.id_utilisateur || currentUser.id;
            const token = localStorage.getItem("token");
            
            const response = await fetch(
                `${API_BASE_URL}/utilisateurs.php?action=profil&user_id=${userId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setUser(result.data);
            } else {
                setError(result.message || "Erreur lors du chargement");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUser(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!user.nom_utilisateur || !user.prenom_utilisateur || !user.email) {
            setError("Veuillez remplir tous les champs obligatoires");
            return;
        }

        setSaving(true);
        setError("");
        setSuccess("");

        try {
            const currentUser = getCurrentUser();
            const userId = currentUser.id_utilisateur || currentUser.id;
            const token = localStorage.getItem("token");
            
            const response = await fetch(
                `${API_BASE_URL}/update_profil.php?user_id=${userId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(user)
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                // Mettre à jour le localStorage avec les nouvelles données
                const updatedUser = { ...currentUser, ...user };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                
                setSuccess("Profil mis à jour avec succès !");
                setTimeout(() => {
                    navigate('/pages/profile');
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de la mise à jour");
            }
        } catch (err) {
            setError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    // Obtenir les initiales pour l'avatar
    const getUserInitials = () => {
        const prenom = user.prenom_utilisateur || '';
        const nom = user.nom_utilisateur || '';
        return `${prenom.charAt(0) || ''}${nom.charAt(0) || ''}`.toUpperCase() || 'U';
    };

    // Obtenir le nom complet
    const getFullName = () => {
        return `${user.prenom_utilisateur || ''} ${user.nom_utilisateur || ''}`.trim() || 'Utilisateur';
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement de votre profil...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-edit-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Modifier le profil</h4>
                                    <p className="text-muted small mb-0">{getFullName()}</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/pages/profile')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Alertes modernes */}
                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {success}
                        </Alert>
                    )}

                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-user-settings-line me-2"></i>
                                            Informations personnelles
                                        </h5>
                                        <Badge className="rounded-pill px-3 py-2" style={{ background: 'white', color: '#fff' }}>
                                            <i className="ri-shield-check-line me-1"></i>
                                            Modifiable
                                        </Badge>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    {/* Avatar preview */}
                                    <div className="text-center mb-4">
                                        <div className="avatar-preview">
                                            {getUserInitials()}
                                        </div>
                                        <h6 className="mt-2 mb-0 fw-semibold">{getFullName()}</h6>
                                        <p className="text-muted small mb-0">{user.role || 'Utilisateur'}</p>
                                    </div>

                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">
                                            {/* SECTION 1 : Identité */}
                                            <Col md={12}>
                                                <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                                                    <i className="ri-user-line me-2"></i>
                                                    Identité
                                                </h6>
                                            </Col>
                                            
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">
                                                        Nom <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-user-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="nom_utilisateur"
                                                            value={user.nom_utilisateur}
                                                            onChange={handleChange}
                                                            required
                                                            className="border-2 rounded-end-3"
                                                            placeholder="Votre nom"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">
                                                        Prénom <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-user-smile-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="prenom_utilisateur"
                                                            value={user.prenom_utilisateur}
                                                            onChange={handleChange}
                                                            required
                                                            className="border-2 rounded-end-3"
                                                            placeholder="Votre prénom"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION 2 : Contact */}
                                            <Col md={12}>
                                                <h6 className="mb-3 mt-2 fw-semibold" style={{ color: '#21ACCD' }}>
                                                    <i className="ri-mail-line me-2"></i>
                                                    Contact
                                                </h6>
                                            </Col>
                                            
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">
                                                        Email <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-mail-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            name="email"
                                                            value={user.email}
                                                            onChange={handleChange}
                                                            required
                                                            className="border-2 rounded-end-3"
                                                            placeholder="votre@email.com"
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        Nous ne partagerons jamais votre email.
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Téléphone</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-phone-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="tel"
                                                            name="telephone"
                                                            value={user.telephone || ''}
                                                            onChange={handleChange}
                                                            className="border-2 rounded-end-3"
                                                            placeholder="+33 6 12 34 56 78"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION 3 : Adresse */}
                                            <Col md={12}>
                                                <h6 className="mb-3 mt-2 fw-semibold" style={{ color: '#21ACCD' }}>
                                                    <i className="ri-map-pin-line me-2"></i>
                                                    Adresse
                                                </h6>
                                            </Col>
                                            
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Adresse</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-home-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            name="adresse"
                                                            value={user.adresse || ''}
                                                            onChange={handleChange}
                                                            className="border-2 rounded-end-3"
                                                            placeholder="Votre adresse complète"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION 4 : Actions */}
                                            <Col md={12}>
                                                <hr className="my-3" />
                                                <div className="d-flex justify-content-end gap-3">
                                                    <Button 
                                                        variant="outline-secondary" 
                                                        onClick={() => navigate('/pages/profile')}
                                                        className="rounded-pill px-4"
                                                    >
                                                        Annuler
                                                    </Button>
                                                    <Button 
                                                        type="submit" 
                                                        variant="primary"
                                                        disabled={saving}
                                                        className="rounded-pill px-5"
                                                        style={{ background: '#21ACCD', border: 'none' }}
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Enregistrement...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-save-line me-2"></i>
                                                                Enregistrer
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>

                            {/* Carte d'information complémentaire */}
                            <Card className="border-0 shadow-sm rounded-3 mt-4">
                                <Card.Body className="p-4">
                                    <h6 className="fw-bold mb-3" style={{ color: '#21ACCD' }}>
                                        <i className="ri-information-line me-2"></i>
                                        Conseils de sécurité
                                    </h6>
                                    <Row>
                                        <Col md={4} className="mb-3 mb-md-0">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                                                    <i className="ri-shield-check-line" style={{ color: '#21ACCD' }}></i>
                                                </div>
                                                <small>Utilisez un email valide</small>
                                            </div>
                                        </Col>
                                        <Col md={4} className="mb-3 mb-md-0">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                                                    <i className="ri-lock-line" style={{ color: '#21ACCD' }}></i>
                                                </div>
                                                <small>Protégez vos informations</small>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="d-flex align-items-center">
                                                <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                                                    <i className="ri-check-line" style={{ color: '#21ACCD' }}></i>
                                                </div>
                                                <small>Vérifiez avant d'enregistrer</small>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .avatar-preview {
                    width: 100px;
                    height: 100px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 36px;
                    font-weight: bold;
                    margin: 0 auto;
                    border: 4px solid white;
                    box-shadow: 0 10px 30px rgba(33, 172, 205, 0.3);
                }
                
                .form-label {
                    font-size: 0.85rem;
                    margin-bottom: 0.5rem;
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
            `}</style>
        </>
    );
}